import React, { useState } from "react";
import { Link } from "react-router-dom";
import Popover from "@mui/material/Popover";
import { Box, List, ListItem } from "@mui/material";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import logo from "./scan-logo.png";

function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);

  // Hover popover anchors
  const [blockchainEl, setBlockchainEl] = useState(null);
  const [tokensEl, setTokensEl] = useState(null);
  const [nftsEl, setNftsEl] = useState(null);
  const [resourcesEl, setResourcesEl] = useState(null);
  const [developersEl, setDevelopersEl] = useState(null);
  const [moreEl, setMoreEl] = useState(null);

  const popoverProps = {
    anchorOrigin: { vertical: "bottom", horizontal: "left" },
    transformOrigin: { vertical: "top", horizontal: "left" },
    disableRestoreFocus: true,
    sx: { pointerEvents: "none" },
    PaperProps: {
      sx: {
        pointerEvents: "auto",
        background: "var(--term-panel)",
        border: "1px solid var(--term-border)",
        borderRadius: 0,
        boxShadow: "0 8px 24px rgba(0,0,0,0.6)",
        marginTop: "2px",
        minWidth: "200px",
        fontFamily: "var(--term-font)",
      },
    },
  };

  // Items may be a plain string (inert placeholder), or an object:
  //   { label, to }   -> internal react-router link
  //   { label, href } -> external link
  const TerminalMenu = ({ items }) => (
    <List sx={{ py: "4px" }}>
      {items.map((item, i) => {
        const entry = typeof item === "string" ? { label: item } : item;
        return (
          <ListItem key={i} className="term-menu-item">
            {entry.to ? (
              <Link to={entry.to}>{entry.label}</Link>
            ) : entry.href ? (
              <a href={entry.href} target="_blank" rel="noreferrer">{entry.label}</a>
            ) : (
              <a>{entry.label}</a>
            )}
          </ListItem>
        );
      })}
    </List>
  );

  return (
    <>
      <header className="term-header">
        <div className="term-header-inner">
          {/* Brand */}
          <Link to="/" className="term-brand">
            <img src={logo} alt="Pecuscan" className="term-brand-logo" />
            <div className="term-brand-text">
              <span className="term-brand-name">PECUSCAN</span>
              <span className="term-brand-sub">PECU NOVUS · MAINNET</span>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="term-nav">
            <Link to="/" className="term-nav-link">HOME</Link>

            <div
              className="term-nav-link"
              onMouseEnter={(e) => setBlockchainEl(e.currentTarget)}
              onMouseLeave={() => setBlockchainEl(null)}
            >
              BLOCKCHAIN <KeyboardArrowDownOutlinedIcon sx={{ fontSize: 14 }} />
              <Popover
                {...popoverProps}
                open={Boolean(blockchainEl)}
                anchorEl={blockchainEl}
                onClose={() => setBlockchainEl(null)}
              >
                <TerminalMenu items={["Top Accounts", "View Blocks", "View Transactions", "Verified Contracts", "Gas Tracker"]} />
              </Popover>
            </div>

            <div
              className="term-nav-link"
              onMouseEnter={(e) => setTokensEl(e.currentTarget)}
              onMouseLeave={() => setTokensEl(null)}
            >
              TOKENS <KeyboardArrowDownOutlinedIcon sx={{ fontSize: 14 }} />
              <Popover
                {...popoverProps}
                open={Boolean(tokensEl)}
                anchorEl={tokensEl}
                onClose={() => setTokensEl(null)}
              >
                <TerminalMenu items={["Top Tokens (PNP16)", "Token Transfers", "Token Approvals"]} />
              </Popover>
            </div>

            <div
              className="term-nav-link"
              onMouseEnter={(e) => setNftsEl(e.currentTarget)}
              onMouseLeave={() => setNftsEl(null)}
            >
              NFTS <KeyboardArrowDownOutlinedIcon sx={{ fontSize: 14 }} />
              <Popover
                {...popoverProps}
                open={Boolean(nftsEl)}
                anchorEl={nftsEl}
                onClose={() => setNftsEl(null)}
              >
                <TerminalMenu items={["Top NFTs", "Latest Mints", "Latest Trades"]} />
              </Popover>
            </div>

            <div
              className="term-nav-link"
              onMouseEnter={(e) => setResourcesEl(e.currentTarget)}
              onMouseLeave={() => setResourcesEl(null)}
            >
              RESOURCES <KeyboardArrowDownOutlinedIcon sx={{ fontSize: 14 }} />
              <Popover
                {...popoverProps}
                open={Boolean(resourcesEl)}
                anchorEl={resourcesEl}
                onClose={() => setResourcesEl(null)}
              >
                <TerminalMenu items={["Charts & Stats", "Network Status", "Validator Nodes", "Branding"]} />
              </Popover>
            </div>

            <div
              className="term-nav-link"
              onMouseEnter={(e) => setDevelopersEl(e.currentTarget)}
              onMouseLeave={() => setDevelopersEl(null)}
            >
              DEVELOPERS <KeyboardArrowDownOutlinedIcon sx={{ fontSize: 14 }} />
              <Popover
                {...popoverProps}
                open={Boolean(developersEl)}
                anchorEl={developersEl}
                onClose={() => setDevelopersEl(null)}
              >
                <TerminalMenu
                  items={[
                    "API Documentation",
                    { label: "RPC Endpoints", to: "/rpc" },
                    "GitHub",
                    "Verify Contract",
                  ]}
                />
              </Popover>
            </div>

            <div
              className="term-nav-link"
              onMouseEnter={(e) => setMoreEl(e.currentTarget)}
              onMouseLeave={() => setMoreEl(null)}
            >
              MORE <KeyboardArrowDownOutlinedIcon sx={{ fontSize: 14 }} />
              <Popover
                {...popoverProps}
                open={Boolean(moreEl)}
                anchorEl={moreEl}
                onClose={() => setMoreEl(null)}
                PaperProps={{
                  sx: { ...popoverProps.PaperProps.sx, minWidth: "320px", p: "12px" },
                }}
              >
                <Box>
                  <h4 className="term-more-heading">▸ TOOLS & SERVICES</h4>
                  <p className="term-more-desc">
                    Discover more of Pecuscan's tools and services in one place.
                  </p>
                  <List sx={{ py: 0, mt: 1 }}>
                    <ListItem className="term-menu-item">
                      <a href="https://www.pecunovus.com" target="_blank" rel="noreferrer">PECU NOVUS MAIN SITE</a>
                    </ListItem>
                    <ListItem className="term-menu-item">
                      <a href="https://www.pecunovus.net" target="_blank" rel="noreferrer">PECU TERMINAL</a>
                    </ListItem>
                    <ListItem className="term-menu-item">
                      <a href="https://docs.pecunovus.com" target="_blank" rel="noreferrer">DOCUMENTATION</a>
                    </ListItem>
                  </List>
                </Box>
              </Popover>
            </div>
          </nav>

          {/* Right side */}
          <div className="term-header-right">
            <a className="term-signin" href="https://www.pecunovus.net" target="_blank" rel="noreferrer">
              <AccountCircleOutlinedIcon sx={{ fontSize: 14 }} />
              <span>SIGN IN</span>
            </a>
            <button
              className="term-mobile-toggle"
              onClick={() => setMobileOpen(true)}
              aria-label="menu"
            >
              <MenuIcon />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="term-mobile-drawer">
          <div className="term-mobile-head">
            <Link to="/" className="term-brand" onClick={() => setMobileOpen(false)}>
              <img src={logo} alt="Pecuscan" className="term-brand-logo" />
              <span className="term-brand-name">PECUSCAN</span>
            </Link>
            <button className="term-mobile-toggle" onClick={() => setMobileOpen(false)}>
              <CloseIcon />
            </button>
          </div>
          <nav className="term-mobile-nav">
            <Link to="/" onClick={() => setMobileOpen(false)}>HOME</Link>
            <a>BLOCKCHAIN</a>
            <a>TOKENS</a>
            <a>NFTS</a>
            <a>RESOURCES</a>
            <a>DEVELOPERS</a>
            <Link to="/rpc" onClick={() => setMobileOpen(false)}>
              &nbsp;&nbsp;▸ RPC ENDPOINTS
            </Link>
            <a>MORE</a>
            <div className="term-mobile-divider" />
            <a href="https://www.pecunovus.net" target="_blank" rel="noreferrer">SIGN IN</a>
          </nav>
        </div>
      )}
    </>
  );
}

export default Navbar;